import { useState } from 'react'

import { BiChevronDown, BiChevronUp } from 'react-icons/bi'

import {
  Box,
  Button,
  Collapse,
  Flex,
  Icon,
  Text,
  VStack,
} from '@chakra-ui/react'

import { ChatActor, type ChatActorType, type SourceI } from '../types'
import { SimpleMarkdown } from './SimpleMarkdown'

interface ChatMessageIProps {
  text: string
  actor: ChatActorType
  sources?: SourceI[]
}

interface SourceCardIProps {
  content: string
}

const SourceCard = ({ content }: SourceCardIProps) => (
  <Box
    bg='bg.500'
    borderLeft='2px solid'
    borderLeftColor='border.100'
    borderRadius='4px'
    fontSize='12px'
    lineHeight='1.5'
    p={3}
  >
    <Text color='text.secondary.light' noOfLines={4}>
      {content}
    </Text>
  </Box>
)

const SourcesCollapsible = ({ sources }: { sources: SourceI[] }) => {
  const [isOpen, setIsOpen] = useState(false)

  if (!sources.length) return null

  return (
    <Box mt={2}>
      <Button
        alignItems='center'
        color='text.secondary.light'
        display='flex'
        fontSize='12px'
        fontWeight={500}
        gap={1}
        h='auto'
        onClick={() => setIsOpen(!isOpen)}
        p={0}
        variant='unstyled'
      >
        {sources.length} source{sources.length !== 1 ? 's' : ''}
        <Icon as={isOpen ? BiChevronUp : BiChevronDown} boxSize='14px' />
      </Button>
      <Collapse animateOpacity in={isOpen}>
        <VStack align='stretch' mt={2} spacing={2}>
          {sources.map((source, idx) => (
            <SourceCard content={source.content} key={idx} />
          ))}
        </VStack>
      </Collapse>
    </Box>
  )
}

export const ChatMessage = ({ text, actor, sources }: ChatMessageIProps) => {
  const isUser = actor === ChatActor.USER

  return (
    <Flex justify={isUser ? 'flex-end' : 'flex-start'} w='100%'>
      <Box
        bg={isUser ? 'brand.100' : 'bg.100'}
        borderBottomLeftRadius={isUser ? 'lg' : '2px'}
        borderBottomRightRadius={isUser ? '2px' : 'lg'}
        borderTopLeftRadius='lg'
        borderTopRightRadius='lg'
        maxW='85%'
        px={4}
        py={3}
      >
        {isUser ? (
          <Text
            color='white'
            fontSize='14px'
            lineHeight='1.6'
            whiteSpace='pre-wrap'
          >
            {text}
          </Text>
        ) : (
          <Box
            color='text.primary.light'
            fontSize='14px'
            lineHeight='1.6'
            sx={{
              '& p:last-child': { mb: 0 },
            }}
          >
            <SimpleMarkdown text={text} />
          </Box>
        )}
        {!isUser && sources && <SourcesCollapsible sources={sources} />}
      </Box>
    </Flex>
  )
}
